/* eslint-disable */
import { Injectable } from '@angular/core';
import { Util } from '@libs/utilities/util';
import { AbstractExtensionPattern } from '@libs/libraries/shared';

import { NgChangeContext } from '../shared/customizable-slider.data';
import { NgCustomizableSliderComponent } from '../customizable-slider.component';
import { EnumNgLabelType, EnumNgPointerType, INgCustomStepDefinition, TypeNgPositionToValueFunction, TypeNgValueToPositionFunction } from '../shared/customizable-slider.interface';


@Injectable()
export class NgSliderHelperExtension extends AbstractExtensionPattern<NgCustomizableSliderComponent> {

	//** get current tracking value */
	getCurrentTrackingValue(): number {
		if (this.main.currentTrackingPointer === EnumNgPointerType.Min) return this.main.viewLowValue;
		if (this.main.currentTrackingPointer === EnumNgPointerType.Max) return this.main.viewHighValue;
		return null!;
	}

	//** get modal value */
	modelValueToViewValue(modelValue: number): number {
		if (Util.Basic.isUndefined(modelValue)) return NaN;
		if (Util.Basic.isDefined(this.main.viewOptions.stepsArray) && !this.main.viewOptions.bindIndexForStepsArray) return this.main.customizableSliderHelper.findStepIndex(+modelValue, this.main.viewOptions.stepsArray!);
		return +modelValue;
	}

	//** get step value */
	getStepValue(sliderValue: number): number {
		const step: INgCustomStepDefinition = this.main.viewOptions?.stepsArray![sliderValue];
		return (Util.Basic.isDefined(step)) ? step.value : NaN;
	}

	applyViewChange() {

		//0 - get modal value
		this.main.value = this.viewValueToModelValue(this.main.viewLowValue);
		if (this.main.range) this.main.highValue = this.viewValueToModelValue(this.main.viewHighValue);

		//1 - update output model change subject
		this.main.outputModelChangeSubject.next({
			value				: this.main.value,
			highValue			: this.main.highValue,
			userEventInitiated	: true,
			forceChange			: false
		});

		//2 - update input model change subject
		this.main.inputModelChangeSubject.next({
			value				: this.main.value,
			highValue			: this.main.highValue,
			forceChange			: false,
			internalChange		: true
		});
	}


	/**------------------------------------------------------
	 * Value Helpers
	 */
	getDisplayValue(value: number, which: EnumNgLabelType): string {
		if (Util.Basic.isDefined(this.main.viewOptions.stepsArray) && !this.main.viewOptions.bindIndexForStepsArray) value = this.main.sliderHelperExtension.getStepValue(value);
		return this.main.viewOptions.translate!(value, which);
	}

	roundStep(value: number, customStep?: number): number {

		//0 - get current step
		const step: number = Util.Basic.isDefined(customStep) ? customStep! : this.main.viewOptions.step!;

		//1 - get step difference
		let steppedDifference: number = this.main.customizableSliderHelper.roundToPrecisionLimit((value - this.main.viewOptions.floor!) / step, this.main.viewOptions.precisionLimit!);
		steppedDifference = Math.round(steppedDifference) * step;

		//2 - return precision limit of step
		return this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewOptions.floor! + steppedDifference, this.main.viewOptions.precisionLimit!);
	}

	valueToPosition(value: number): number {

		//0 - value to position function
		let fn: TypeNgValueToPositionFunction = this.main.customizableSliderHelper.linearValueToPosition;
		if (Util.Basic.isDefined(this.main.viewOptions.customValueToPosition)) fn = this.main.viewOptions.customValueToPosition!;
			else if (this.main.viewOptions.logScale) fn = this.main.customizableSliderHelper.logValueToPosition;

		//1 - update value
		value = this.main.customizableSliderHelper.clampToRange(value, this.main.viewOptions.floor!, this.main.viewOptions.ceil!);
		let percent: number = fn(value, this.main.viewOptions.floor!, this.main.viewOptions.ceil!);

		//2 - update position in percent
		if (Util.Basic.isUndefined(percent)) percent = 0;
		if (this.main.viewOptions.rightToLeft) percent = 1 - percent;
		return percent * this.main.maxHandlePosition;
	}

	positionToValue(position: number): number {

		//0 - get percent
		let percent: number = position / this.main.maxHandlePosition;
		if (this.main.viewOptions.rightToLeft) percent = 1 - percent;

		//1 - update position to value function
		let fn: TypeNgPositionToValueFunction = this.main.customizableSliderHelper.linearPositionToValue;
		if (Util.Basic.isDefined(this.main.viewOptions.customPositionToValue)) fn = this.main.viewOptions.customPositionToValue!;
			else if (this.main.viewOptions.logScale) fn = this.main.customizableSliderHelper.logPositionToValue;

		//2 - update value
		const value: number = fn(percent, this.main.viewOptions.floor!, this.main.viewOptions.ceil!);
		return Util.Basic.isDefined(value) ? value : 0;
	}

	getEventPosition(event: MouseEvent|TouchEvent, targetTouchId?: number): number {

		//0 - slider element bounding rect
		const sliderElementBoundingRect : DOMRect = this.main.elementRef.nativeElement.getBoundingClientRect();
		const sliderPos					: number = (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0) ?
		sliderElementBoundingRect.bottom: sliderElementBoundingRect.left;

		//1 - update event element
		let eventPos: number = 0;
		if (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0) eventPos = -this.getEventXY(event, targetTouchId) + sliderPos;
			else eventPos = this.getEventXY(event, targetTouchId) - sliderPos;

		//2 - update position element
		return eventPos * this.main.viewOptions.scale! - this.main.handleHalfDimension;
	}

	//** Bind event on start */
	onBarStart(pointerType: EnumNgPointerType, draggableRange: boolean, event: MouseEvent|TouchEvent, bindMove: boolean, bindEnd: boolean, simulateImmediateMove?: boolean, simulateImmediateEnd?: boolean) {
		if (draggableRange) this.main.sliderEventsExtension.onDragStart(pointerType, event, bindMove, bindEnd);
			else this.main.sliderEventsExtension.onStart(pointerType, event, bindMove, bindEnd, simulateImmediateMove, simulateImmediateEnd);
	}


	/**------------------------------------------------------
	 * Min & Max Value Helper
	 */
	getMinValue(newPos: number, outOfBounds: boolean, isAbove: boolean): number {

		//0 - check is rtl
		const isRTL: boolean = this.main.viewOptions.rightToLeft!;

		//1 - update value
		let value: number = null!;
		if (outOfBounds) {
			if (isAbove) {
				value = isRTL
					? this.main.viewOptions.floor!
					: this.main.viewOptions.ceil! - this.main.dragging.difference;
			} else {
				value = isRTL
					? this.main.viewOptions.ceil! - this.main.dragging.difference
					: this.main.viewOptions.floor!;
			}
		} else {
			value = isRTL
				? this.main.sliderHelperExtension.positionToValue(newPos + this.main.dragging.lowLimit)
				: this.main.sliderHelperExtension.positionToValue(newPos - this.main.dragging.lowLimit);
		}

		//2 - update round step
		return this.main.sliderHelperExtension.roundStep(value);
	}

	getMaxValue(newPos: number, outOfBounds: boolean, isAbove: boolean): number {

		//0 - check is rtl
		const isRTL: boolean = this.main.viewOptions.rightToLeft!;

		//1 - update value
		let value: number = null!;
		if (outOfBounds) {
			if (isAbove) {
				value = isRTL
					? this.main.viewOptions.floor! + this.main.dragging.difference
					: this.main.viewOptions.ceil!;
			} else {
				value = isRTL
					? this.main.viewOptions.ceil!
					: this.main.viewOptions.floor! + this.main.dragging.difference;
			}
		} else {
			value = isRTL
				? this.main.sliderHelperExtension.positionToValue(newPos + this.main.dragging.lowLimit) + this.main.dragging.difference
				: this.main.sliderHelperExtension.positionToValue(newPos - this.main.dragging.lowLimit) + this.main.dragging.difference;
		}

		//2 - update round step
		return this.main.sliderHelperExtension.roundStep(value);
	}


	/**------------------------------------------------------
	 * Position Tracking Helper
	 */
	positionTrackingBar(newMinValue: number, newMaxValue: number) {

		//0 - on min limit defined and valid min value
		if (Util.Basic.isDefined(this.main.viewOptions.minLimit) && newMinValue < this.main.viewOptions.minLimit!) {
			newMinValue = this.main.viewOptions.minLimit!;
			newMaxValue = this.main.customizableSliderHelper.roundToPrecisionLimit(newMinValue + this.main.dragging.difference, this.main.viewOptions.precisionLimit!);
		}

		//1 - on max limit defined and valid max value
		if (Util.Basic.isDefined(this.main.viewOptions.maxLimit) && newMaxValue > this.main.viewOptions.maxLimit!) {
			newMaxValue = this.main.viewOptions.maxLimit!;
			newMinValue = this.main.customizableSliderHelper.roundToPrecisionLimit(newMaxValue - this.main.dragging.difference, this.main.viewOptions.precisionLimit!);
		}

		//2 - update min and max value
		this.main.viewLowValue 	= newMinValue;
		this.main.viewHighValue = newMaxValue;

		//3 - apply view changes and update handles
		this.main.sliderHelperExtension.applyViewChange();
		this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Min, this.main.sliderHelperExtension.valueToPosition(newMinValue));
		this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Max, this.main.sliderHelperExtension.valueToPosition(newMaxValue));
	}

	positionTrackingHandle(newValue: number) {

		//0 - get new min and max limit
		newValue = this.applyMinMaxLimit(newValue);

		//1 - check main component range
		if (this.main.range) {

			// update new value on push range
			if (this.main.viewOptions.pushRange) {
				newValue = this.applyPushRange(newValue);
			} else {

				//a. update value if no switching
				if (this.main.viewOptions.noSwitching) {
					if (this.main.currentTrackingPointer === EnumNgPointerType.Min && newValue > this.main.viewHighValue) newValue = this.applyMinMaxRange(this.main.viewHighValue);
						else if (this.main.currentTrackingPointer === EnumNgPointerType.Max && newValue < this.main.viewLowValue) newValue = this.applyMinMaxRange(this.main.viewLowValue);
				}

				//b. update new value
				newValue = this.applyMinMaxRange(newValue);

				//c. update tracking pointer
				if (this.main.currentTrackingPointer === EnumNgPointerType.Min && newValue > this.main.viewHighValue) {
					this.main.viewLowValue = this.main.viewHighValue;
					this.main.sliderHelperExtension.applyViewChange();
					this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Min, this.main.maxHandleElement.position);
					this.main.sliderValueUpdateExtension.updateAriaAttributes();
					this.main.currentTrackingPointer 	= EnumNgPointerType.Max;
					this.main.minHandleElement.active 	= false;
					this.main.maxHandleElement.active 	= true;
					if (this.main.viewOptions.keyboardSupport) this.main.maxHandleElement.focus();
				} else if (this.main.currentTrackingPointer === EnumNgPointerType.Max && newValue < this.main.viewLowValue) {
					this.main.viewHighValue = this.main.viewLowValue;
					this.main.sliderHelperExtension.applyViewChange();
					this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Max, this.main.minHandleElement.position);
					this.main.sliderValueUpdateExtension.updateAriaAttributes();
					this.main.currentTrackingPointer 	= EnumNgPointerType.Min;
					this.main.maxHandleElement.active 	= false;
					this.main.minHandleElement.active 	= true;
					if (this.main.viewOptions.keyboardSupport) this.main.minHandleElement.focus();
				}
			}
		}

		//2 - update low and apply view value
		if (this.main.sliderHelperExtension.getCurrentTrackingValue() !== newValue) {

			//a. set low and view value
			if (this.main.currentTrackingPointer === EnumNgPointerType.Min) {
				this.main.viewLowValue = newValue;
				this.main.sliderHelperExtension.applyViewChange();
			} else if (this.main.currentTrackingPointer === EnumNgPointerType.Max) {
				this.main.viewHighValue = newValue;
				this.main.sliderHelperExtension.applyViewChange();
			}

			//b. update handles and aria attributes
			this.main.sliderHandlerExtension.updateHandles(this.main.currentTrackingPointer, this.main.sliderHelperExtension.valueToPosition(newValue));
			this.main.sliderValueUpdateExtension.updateAriaAttributes();
		}
	}

	getChangeContext(): NgChangeContext {

		//0 - get new change context
		const changeContext: NgChangeContext = new NgChangeContext();
		changeContext.pointerType = this.main.currentTrackingPointer;
		changeContext.value = +this.main.value;

		//1 - update hight value
		if (this.main.range) changeContext.highValue = +this.main.highValue;

		//2 - return change context
		return changeContext;
	}


	/**------------------------------------------------------
	 * Helper Function
	 */
	private viewValueToModelValue(viewValue: number): number {
		if (Util.Basic.isDefined(this.main.viewOptions.stepsArray) && !this.main.viewOptions.bindIndexForStepsArray) return this.getStepValue(viewValue);
		return viewValue;
	}

	private getEventXY(event: MouseEvent|TouchEvent, targetTouchId?: number): number {

		//0 - check if mouse event
		if (event instanceof MouseEvent) return (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0) ? event.clientY : event.clientX;

		//1 - set default touch index and get touches
		let   touchIndex: number 	= 0;
		const touches	: TouchList = event.touches;

		//2 - update touch index if target touch id defined
		if (Util.Basic.isDefined(targetTouchId)) {
			for (let i: number = 0; i < touches.length; i++) {
				if (touches[i].identifier !== targetTouchId) continue;
				touchIndex = i;
				break;
			}
		}

		//3 - get axes
		return (this.main.viewOptions.vertical || this.main.viewOptions.rotate !== 0)
			? touches[touchIndex].clientY
			: touches[touchIndex].clientX;
	}

	//** Apply min max limit */
	private applyMinMaxLimit(newValue: number): number {
		if (Util.Basic.isDefined(this.main.viewOptions.minLimit) && newValue < this.main.viewOptions.minLimit!) return this.main.viewOptions.minLimit!;
		if (Util.Basic.isDefined(this.main.viewOptions.maxLimit) && newValue > this.main.viewOptions.maxLimit!) return this.main.viewOptions.maxLimit!;
		return newValue;
	}

	private applyMinMaxRange(newValue: number): number {

		//0 - get opposite value and difference
		const oppositeValue	: number = (this.main.currentTrackingPointer === EnumNgPointerType.Min) ? this.main.viewHighValue : this.main.viewLowValue;
		const difference	: number = Math.abs(newValue - oppositeValue);

		//1 - if min range
		if (Util.Basic.isDefined(this.main.viewOptions.minRange) && difference < this.main.viewOptions.minRange!) {
			if (this.main.currentTrackingPointer === EnumNgPointerType.Min) return this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewHighValue - this.main.viewOptions.minRange!, this.main.viewOptions.precisionLimit!);
				else if (this.main.currentTrackingPointer === EnumNgPointerType.Max) return this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewLowValue + this.main.viewOptions.minRange!, this.main.viewOptions.precisionLimit!);
		}

		//2 - if max range
		if (Util.Basic.isDefined(this.main.viewOptions.maxRange) && difference > this.main.viewOptions.maxRange!) {
			if (this.main.currentTrackingPointer === EnumNgPointerType.Min) return this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewHighValue - this.main.viewOptions.maxRange!, this.main.viewOptions.precisionLimit!);
				else if (this.main.currentTrackingPointer === EnumNgPointerType.Max) return this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewLowValue + this.main.viewOptions.maxRange!, this.main.viewOptions.precisionLimit!);
		}

		//3 - return new value
		return newValue;
	}

	private applyPushRange(newValue: number): number {

		//0 - get difference, min and max range
		const difference: number = (this.main.currentTrackingPointer === EnumNgPointerType.Min) ? this.main.viewHighValue - newValue : newValue - this.main.viewLowValue;
		const minRange: number = (Util.Basic.isDefined(this.main.viewOptions.minRange)) ? this.main.viewOptions.minRange! : this.main.viewOptions.step!;
		const maxRange: number = this.main.viewOptions.maxRange!;

		//1 - if difference is less than min range
		if (difference < minRange) {

			//a. on min current tracker
			if (this.main.currentTrackingPointer === EnumNgPointerType.Min) {
				this.main.viewHighValue = this.main.customizableSliderHelper.roundToPrecisionLimit(
				Math.min(newValue + minRange, this.main.viewOptions.ceil!), this.main.viewOptions.precisionLimit!);
				newValue = this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewHighValue - minRange, this.main.viewOptions.precisionLimit!);
				this.main.sliderHelperExtension.applyViewChange();
				this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Max, this.main.sliderHelperExtension.valueToPosition(this.main.viewHighValue));
			}

			//b. on max current tracker
			else if (this.main.currentTrackingPointer === EnumNgPointerType.Max) {
				this.main.viewLowValue = this.main.customizableSliderHelper.roundToPrecisionLimit(
				Math.max(newValue - minRange, this.main.viewOptions.floor!), this.main.viewOptions.precisionLimit!);
				newValue = this.main.customizableSliderHelper.roundToPrecisionLimit(this.main.viewLowValue + minRange, this.main.viewOptions.precisionLimit!);
				this.main.sliderHelperExtension.applyViewChange();
				this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Min, this.main.sliderHelperExtension.valueToPosition(this.main.viewLowValue));
			}

			//c. update aria attributes
			this.main.sliderValueUpdateExtension.updateAriaAttributes();
		}

		//2 - if difference is greater than max range
		else if (Util.Basic.isDefined(maxRange) && difference > maxRange) {

			//a. on min tracking pointer
			if (this.main.currentTrackingPointer === EnumNgPointerType.Min) {
				this.main.viewHighValue = this.main.customizableSliderHelper.roundToPrecisionLimit(newValue + maxRange, this.main.viewOptions.precisionLimit!);
				this.main.sliderHelperExtension.applyViewChange();
				this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Max, this.main.sliderHelperExtension.valueToPosition(this.main.viewHighValue)
				);
			}
			//b. on max tracking pointer
			else if (this.main.currentTrackingPointer === EnumNgPointerType.Max) {
				this.main.viewLowValue = this.main.customizableSliderHelper.roundToPrecisionLimit(newValue - maxRange, this.main.viewOptions.precisionLimit!);
				this.main.sliderHelperExtension.applyViewChange();
				this.main.sliderHandlerExtension.updateHandles(EnumNgPointerType.Min, this.main.sliderHelperExtension.valueToPosition(this.main.viewLowValue));
			}

			//c. update area attributes
			this.main.sliderValueUpdateExtension.updateAriaAttributes();
		}

		//3 - return new value
		return newValue;
	}
}
